/**
 * News detail modal with AI-generated Axios-style summary
 */

import { useEffect, useState } from 'react';
import { getNewsDetail, type NewsDetailResponse } from '../../services/api';

interface NewsDetailModalProps {
  eventId: string | null;
  onClose: () => void;
}

export function NewsDetailModal({ eventId, onClose }: NewsDetailModalProps) {
  const [data, setData] = useState<NewsDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    getNewsDetail(eventId)
      .then((data) => {
        setData(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(message);
        setLoading(false);
      });
  }, [eventId]);

  if (!eventId) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 z-10"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {loading ? (
            <div className="p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          ) : error ? (
            <div className="p-8">
              <div className="text-center text-red-600">
                <p>{error}</p>
                <button
                  onClick={onClose}
                  className="mt-4 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          ) : data ? (
            <div className="p-6 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <span className="font-medium text-primary-600">{data.event.company_name}</span>
                  <span>•</span>
                  <span className="capitalize">{data.event.event_type.replace(/_/g, ' ')}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {data.analysis.headline}
                </h2>
              </div>

              {/* Why It Matters */}
              <section className="bg-primary-50 rounded-lg p-4">
                <h3 className="text-sm font-bold text-primary-900 uppercase tracking-wide mb-2">
                  Why It Matters
                </h3>
                <p className="text-gray-800">{data.analysis.why_it_matters}</p>
              </section>

              {/* The Big Picture */}
              <section>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  The Big Picture
                </h3>
                <p className="text-gray-700">{data.analysis.the_big_picture}</p>
              </section>

              {/* Key Details */}
              <section>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Key Details
                </h3>
                <ul className="space-y-2">
                  {data.analysis.key_details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <span className="text-primary-600 mt-1">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* What's Next */}
              <section>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  What's Next
                </h3>
                <p className="text-gray-700">{data.analysis.whats_next}</p>
              </section>

              {/* Analyst Take */}
              <section className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <h3 className="text-sm font-bold text-yellow-900 uppercase tracking-wide mb-2">
                  Analyst Take
                </h3>
                <p className="text-gray-800 italic">{data.analysis.analyst_take}</p>
              </section>

              {/* Topics */}
              {data.analysis.related_topics.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {data.analysis.related_topics.map((topic, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}

              {/* Source Link */}
              <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Source: {data.analysis.source_name || 'Unknown'}
                </div>
                {data.analysis.source_url && (
                  <a
                    href={data.analysis.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    View Original Source
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default NewsDetailModal;
